import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

const ROLE_COLORS: Record<string, string> = {
  admin: "#3b82f6",
  teacher: "#22c55e",
  parent: "#a855f7",
  super_admin: "#f59e0b",
  support: "#06b6d4",
  accountant: "#ec4899",
  developer: "#6366f1",
  call_center: "#14b8a6",
  supervisor: "#f97316",
};

const ROLE_LABELS: Record<string, string> = {
  admin: "مدير حضانة",
  teacher: "معلم",
  parent: "ولي أمر",
  super_admin: "مشرف عام",
  support: "دعم فني",
  accountant: "محاسب",
  developer: "مطوّر",
  call_center: "مركز اتصال",
  supervisor: "مشرف",
};

interface UserChartsProps {
  users: any[];
}

const UserCharts = ({ users }: UserChartsProps) => {
  // Role distribution
  const roleCounts: Record<string, number> = {};
  users.forEach((u) => u.roles?.forEach((r: string) => {
    roleCounts[r] = (roleCounts[r] || 0) + 1;
  }));
  const roleData = Object.entries(roleCounts).map(([role, count]) => ({
    name: ROLE_LABELS[role] || role,
    value: count,
    color: ROLE_COLORS[role] || "#94a3b8",
  }));

  // Registration trend (last 6 months)
  const monthlyData: Record<string, number> = {};
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    monthlyData[key] = 0;
  }
  users.forEach((u) => {
    const d = new Date(u.created_at);
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    if (key in monthlyData) monthlyData[key]++;
  });
  const trendData = Object.entries(monthlyData).map(([month, count]) => ({
    month: month.slice(5) + "/" + month.slice(0, 4),
    count,
  }));

  // Nursery distribution (top 8)
  const nurseryCounts: Record<string, number> = {};
  users.forEach((u) => {
    const name = (u as any).nurseries?.name || "بدون حضانة";
    nurseryCounts[name] = (nurseryCounts[name] || 0) + 1;
  });
  const nurseryData = Object.entries(nurseryCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([name, count]) => ({ name: name.length > 12 ? name.slice(0, 12) + "…" : name, count }));

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">توزيع الأدوار</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={roleData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={false} fontSize={11}>
                {roleData.map((d, i) => <Cell key={i} fill={d.color} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">التسجيل الشهري</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={trendData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--primary))" radius={[4, 4, 0, 0]} name="مستخدمون" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">أكثر الحضانات مستخدمين</CardTitle>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={nurseryData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" fontSize={11} />
              <YAxis dataKey="name" type="category" fontSize={10} width={90} />
              <Tooltip />
              <Bar dataKey="count" fill="hsl(var(--chart-2))" radius={[0, 4, 4, 0]} name="مستخدمون" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserCharts;
